import React, { useEffect, useState } from "react";
import axios from "axios";
import Card from "../Components/Card";
import SimpleTable from "../Components/SimpleTable";
import { BRAND } from "../css/brand";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, RefreshCw, XCircle } from "lucide-react";

export default function DistrictActivationsCard() {
  const [geoActivation, setGeoActivation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authUserString = sessionStorage.getItem("authUserData1");
  const authUser = JSON.parse(authUserString);
  const loginUserID = authUser?.data[0]?.id;

  // 🔹 Date range state
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // 🔹 Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  // 🔹 Initialize default range (1st day of current month → today)
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setFromDate(firstDay);
    setToDate(today);
  }, []);

  // 🔹 Fetch data
  const fetchData = async () => {
    if (!loginUserID || !fromDate || !toDate) return;

    const from = fromDate.toISOString().split("T")[0];
    const to = toDate.toISOString().split("T")[0];

    try {
      setLoading(true);
      setError(null);

      const url = `https://spillas.com/api/v1/district-wise-activations?from_date=${from}&to_date=${to}&company_id=${loginUserID}`;
      const res = await axios.get(url);

      if (res.data) {
        setGeoActivation(res.data.data || []);
        setCurrentPage(1);
      } else {
        setGeoActivation([]);
      }
    } catch (err) {
      console.error("Error fetching district activations:", err);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch on mount and when date range changes
  useEffect(() => {
    fetchData();
  }, [fromDate, toDate]);

  // 🔹 Reset filter
  const handleReset = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setFromDate(firstDay);
    setToDate(today);
  };

  // 🔹 Pagination logic
  const totalPages = Math.ceil(geoActivation.length / rowsPerPage);
  const paginatedData = geoActivation.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <Card brand={BRAND} title="District-wise Activations (Current Period)">
      {/* 🔹 Date Range Picker */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 justify-center sm:justify-start">
        <label className="text-xs sm:text-sm font-medium text-gray-700 flex items-center gap-1">
          <Calendar className="w-4 h-4 text-blue-700" />
          Date Range:
        </label>

        <div className="border rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 sm:w-64 text-gray-700 focus-within:ring-2 focus-within:ring-blue-400">
          <DatePicker
            selectsRange
            startDate={fromDate}
            endDate={toDate}
            onChange={(dates) => {
              const [start, end] = dates;
              setFromDate(start);
              setToDate(end);
            }}
            dateFormat="yyyy-MM-dd"
            maxDate={new Date()}
            placeholderText="Select date range"
            className="w-full text-xs sm:text-sm text-gray-700 outline-none bg-transparent"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-md text-sm"
          >
            <XCircle size={14} />
            <span className="hidden sm:inline">Reset</span>
          </button>

          <button
            onClick={fetchData}
            className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
          >
            <RefreshCw size={14} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* 🔹 Loading / Error / Table Display */}
      {loading ? (
        <p className="text-center text-slate-500 py-4">Loading data...</p>
      ) : error ? (
        <p className="text-center text-red-500 py-4">{error}</p>
      ) : geoActivation.length === 0 ? (
        <p className="text-center text-slate-500 py-4">No data found.</p>
      ) : (
        <>
          <SimpleTable
            headers={["District", "State", "Activations"]}
            rows={paginatedData.map((g) => [
              g.district,
              g.state,
              g.total_count,
            ])}
          />

          {/* 🔹 Pagination */}
          <div className="flex gap-2 justify-end mt-4 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className={`px-2 py-1 border rounded text-sm ${
                currentPage === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-slate-100"
              }`}
            >
              Prev
            </button>

            {(() => {
              const pages = [];
              const maxVisible = 5;
              let start = Math.max(1, currentPage - 2);
              let end = Math.min(totalPages, start + maxVisible - 1);
              if (end - start < maxVisible - 1) {
                start = Math.max(1, end - maxVisible + 1);
              }
              if (start > 1) {
                pages.push(1);
                if (start > 2) pages.push("...");
              }
              for (let i = start; i <= end; i++) pages.push(i);
              if (end < totalPages) {
                if (end < totalPages - 1) pages.push("...");
                pages.push(totalPages);
              }
              return pages.map((p, idx) =>
                p === "..." ? (
                  <span key={idx} className="px-2 text-gray-500">
                    ...
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`px-2 py-1 border rounded text-sm ${
                      currentPage === p
                        ? "bg-blue-600 text-white"
                        : "bg-white hover:bg-slate-100"
                    }`}
                  >
                    {p}
                  </button>
                )
              );
            })()}

            <button
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
              className={`px-2 py-1 border rounded text-sm ${
                currentPage === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-slate-100"
              }`}
            >
              Next
            </button>
          </div>
        </>
      )}
    </Card>
  );
}



// import React from "react";
// import Card from "../Components/Card";
// import SimpleTable from "../Components/SimpleTable";
// import { BRAND } from "../css/brand";

// export default function DistrictActivationsCard({ geoActivation = [] }) {
//   return (
//     <Card brand={BRAND} title="District-wise Activations (Current Period)">
//       <SimpleTable
//         headers={["District", "State", "Activations"]}
//         rows={geoActivation.map((g) => [g.name, g.state, g.activations])}
//       />
//     </Card>
//   );
// }
